import React, { useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSocratesInterview } from "../../../hooks/useSocratesInterview";
import InterviewResultV2 from "../components/InterviewResultV2";
import { backIncstance } from "../../../utils/commonFunctions";

/**
 * SocratesResultContainer
 * 소크라테스 면접 결과 페이지 컨테이너
 */
const SocratesResultContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    interview,
    messages,
    loading,
    loadInterview,
    restartInterview,
    generateResult,
  } = useSocratesInterview();

  const interviewId = id ? Number(id) : null;

  // 결과 저장 중복 방지를 위한 ref
  const savedResultRef = useRef<boolean>(false);

  // 결과 페이지 진입 시 스크롤을 최상단으로 이동
  useEffect(() => {
    // window 스크롤
    window.scrollTo(0, 0);

    // 스크롤 컨테이너를 찾아서 스크롤 (SocratesV2의 overflow-y-auto div)
    const scrollContainer = document.querySelector(".overflow-y-auto");
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }, []);

  // 면접 데이터 로드
  useEffect(() => {
    if (interviewId) {
      loadInterview(interviewId);
    }
  }, [interviewId, loadInterview]);

  /**
   * 레거시 백엔드에 Jobshopper 결과 저장
   * 면접이 완료(completed)된 상태에서만 저장
   * @param interviewData - 면접 데이터
   * @param messagesData - 메시지 목록 (활동 시간/키워드 통계용)
   */
  const saveResultToLegacy = async (
    interviewData: any,
    messagesData: any[]
  ) => {
    try {
      const criteria = interviewData.result?.assessmentCriteria || {};

      // user role 메시지만 필터링 (사용자 답변만 - 활동 시간/키워드 통계용)
      const userMessages = (messagesData || [])
        .filter((msg: any) => msg.role === "user")
        .map((msg: any) => ({
          content: msg.content,
          created_at: msg.createdAt || msg.created_at,
        }));

      // 각 카테고리 평균 점수 계산 함수
      const calcAvg = (arr: any[]): number | null => {
        if (!arr?.length) return null;
        return Math.round(
          arr.reduce((sum: number, item: any) => sum + (item.score || 0), 0) /
            arr.length
        );
      };

      // 각 카테고리별 점수 계산
      const fluencyScore = calcAvg(criteria["유창성"]);
      const logicScore = calcAvg(criteria["논리성"]);
      const problemSolvingScore = calcAvg(criteria["문제해결"]);
      const accuracyScore = calcAvg(criteria["정확성"]);
      const communicationScore = calcAvg(criteria["의사소통"]);

      // 종합 점수 계산
      const scores = [
        fluencyScore,
        logicScore,
        problemSolvingScore,
        accuracyScore,
        communicationScore,
      ].filter((s): s is number => s !== null);
      const totalScore =
        scores.length > 0
          ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
          : null;

      // 종합 등급 계산
      let totalEvaluation: string | null = null;
      if (totalScore !== null) {
        totalEvaluation =
          totalScore >= 8 ? "우수" : totalScore >= 5 ? "보통" : "노력";
      }

      // Q&A 통계
      const qaPairs = interviewData.result?.questionAnswerPairs || [];
      const questionCount = qaPairs.length;
      const answerCount = qaPairs.filter(
        (qa: any) => qa.answer && qa.answer !== "답변 시간 초과"
      ).length;

      // 제목 생성
      const date = new Date(interviewData.createdAt);
      const title = `${
        date.getMonth() + 1
      }월 ${date.getDate()}일 모의 면접 결과`;

      // 레거시 API 호출
      const response = await backIncstance(
        "POST",
        "/api/v1/socra/jobshopper/result",
        {
          jobshopper_interview_id: interviewData.id,
          title,
          interview_type: interviewData.interviewType || "",
          interview_subject: interviewData.interviewSubject || null,
          target_institution: interviewData.targetInstitution || null,
          fluency_score: fluencyScore,
          logic_score: logicScore,
          problem_solving_score: problemSolvingScore,
          accuracy_score: accuracyScore,
          communication_score: communicationScore,
          total_score: totalScore,
          total_evaluation: totalEvaluation,
          question_count: questionCount,
          answer_count: answerCount,
          messages: userMessages, // 활동 시간/키워드 통계용
        }
      );

      const data = await response.json();
      console.log("[SocratesResult] 레거시 백엔드 결과 저장:", data);
    } catch (error) {
      // 저장 실패해도 결과 페이지 표시에는 영향 없음
      console.error("[SocratesResult] 레거시 백엔드 결과 저장 실패:", error);
    }
  };

  // 면접 결과가 로드되면 레거시 백엔드에 저장
  useEffect(() => {
    // 조건: interview 로드됨, completed 상태, messages 로드됨, 아직 저장 안 함
    if (
      interview &&
      interview.status === "completed" &&
      messages &&
      messages.length > 0 &&
      !savedResultRef.current
    ) {
      savedResultRef.current = true;
      saveResultToLegacy(interview, messages);
    }
  }, [interview, messages]);

  // 다시 면접보기 핸들러
  const handleRestart = async () => {
    try {
      if (restartInterview) {
        await restartInterview();
        // 면접 재시작 후 채팅 페이지로 이동
        navigate(`/socrates?id=${interviewId}`);
      }
    } catch (error) {
      console.error("면접 재시작 실패:", error);
    }
  };

  // 결과 재생성 핸들러
  const handleGenerateResult = async () => {
    try {
      if (generateResult) {
        await generateResult();
      }
    } catch (error) {
      console.error("결과 생성 실패:", error);
    }
  };

  return (
    <InterviewResultV2
      interview={interview}
      messages={messages}
      onRestart={handleRestart}
      onGenerateResult={handleGenerateResult}
    />
  );
};

export default SocratesResultContainer;
